import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DEFAULT_PAGE_SIZE } from '@/__core/constants/pagination';
import { Alert, Box, Snackbar } from '@mui/material';
import { useSelector } from 'react-redux';

import AppListToolbarGlobal from '@/modules/inventory/returns/components/ReturnsToolbarGlobal';
import { resolveActiveCompanyFilter, resolveToolbarTabCompanyFilter } from '@/__core/hooks/useCompanyContext';
import { useModuleTabsWithGuard } from '@/__core/hooks/useModuleTabsWithGuard';
import MainListModule from '@/__core/layout/module/MainListModule';
import { useModuleToolbarPermissions, isTabVisibleForToolbarCompany } from '@/__core/permissions';
import { TabData } from '@/__core/types/TabData';
import ModuleTabsHeader from '@/modules/users/components/ModuleTabsHeader';
import { CompanyData, getCurrentCompanyId } from '@/modules/auth/helpers/CompanyStorage';
import { RootState } from '@/setup/redux/RootReducer';

import { createDefaultReturnForm, returnStatusOptions } from '../returnsMockData';
import { getReturns } from '../redux';
import { InventoryReturnFormData, InventoryReturnRow } from '../types';
import ReturnForm from './ReturnForm';
import ReturnList, { ReturnListHandle } from './ReturnList';

type ReturnCompanyOption = {
  company_id: string;
  company_name: string;
};

const buildFormMetadata = (formData: InventoryReturnFormData, returnId?: string) => ({ formData, returnId });
const PAGE_SIZE = DEFAULT_PAGE_SIZE;
const formStatusOptions = returnStatusOptions.filter((option) => option.id !== 'all');

const resolveCompanyIdForQuery = (company: string) => {
  if (!company || company === 'all' || company === 'global') {
    return '';
  }
  return company;
};

export default function ReturnsModule() {
  const companies = useSelector((state: RootState) => (state.auth.companies || []) as CompanyData[]);
  const hasGlobalRole = useSelector((state: RootState) => state.auth?.hasGlobalRole ?? false);
  const listRef = useRef<ReturnListHandle>(null);

  const companyOptions = useMemo<ReturnCompanyOption[]>(
    () =>
      companies.map((company: CompanyData) => ({
        company_id: company.company_id,
        company_name: company.company_name,
      })),
    [companies]
  );

  const [rows, setRows] = useState<InventoryReturnRow[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);
  const [filters, setFilters] = useState({ company: 'all', status: 'all' });
  const [isAdvancedSearchOpen, setIsAdvancedSearchOpen] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const selectedCompanyId = useMemo(
    () => String(getCurrentCompanyId() || '').trim(),
    [],
  );

  const activeCompanyFilter = useMemo(
    () => resolveActiveCompanyFilter(selectedCompanyId, filters.company),
    [selectedCompanyId, filters.company],
  );

  const toolbarTabCompanyFilter = useMemo(
    () => resolveToolbarTabCompanyFilter(filters.company),
    [filters.company],
  );

  const {
    canCreate: canCreateReturns,
    canUpdate: canUpdateReturns,
    resolveTabPermissions,
    resolvePermissionsForCompanyId,
    canReadRecord,
  } = useModuleToolbarPermissions({
    resource: 'inventory.return',
    activeCompanyFilter,
    updateTabTypes: ['update-return'],
  });

  const defaultTabs: TabData[] = useMemo(() => {
    const tabs: TabData[] = [
      { id: 0, title: 'Danh sách hoàn trả', type: 'list', closable: false, createdAt: Date.now() },
    ];
    if (canCreateReturns) {
      tabs.push({ id: 10, title: '', type: 'add', closable: false, createdAt: Date.now() });
    }
    return tabs;
  }, [canCreateReturns]);

  const {
    storedTabs,
    activeTab,
    updateTab,
    tabs,
    visibleActiveTab,
    handleVisibleTabChange,
    isStoredTabPanelActive,
    handleCloseTab,
    handleAddTab,
    handleUnsavedChangesUpdate,
    guardAndRun,
    openTab,
    unsavedDialog,
  } = useModuleTabsWithGuard({
    storageKey: 'inventory_returns_tabs',
    defaultTabs,
    maxTabs: 12,
    canCreate: Boolean(canCreateReturns),
    toolbarCompanyId: toolbarTabCompanyFilter,
    guardedTabTypes: ['new-return', 'update-return'],
    allowMultipleTabTypes: ['new-return', 'update-return'],
    buildNewTab: (currentTabs) => {
      const baseCompanyId = resolveCompanyIdForQuery(activeCompanyFilter) || selectedCompanyId;
      return {
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: `RT #${currentTabs.filter((tab) => tab.type === 'new-return').length + 1}`,
        type: 'new-return',
        closable: true,
        metadata: {
          companyId: baseCompanyId,
          ...buildFormMetadata(createDefaultReturnForm(baseCompanyId)),
        },
      };
    },
  });

  const activeTabData = storedTabs[activeTab];
  const activeFormData = activeTabData?.metadata?.formData as InventoryReturnFormData | undefined;
  const activeReturnId = activeTabData?.metadata?.returnId as string | undefined;
  const listScopeCompanyId = resolveCompanyIdForQuery(activeCompanyFilter);

  const handleToolbarFilterChange = (field: string, value: string) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const loadReturns = useCallback(async () => {
    setListLoading(true);
    try {
      const response = await getReturns({
        companyId: listScopeCompanyId,
        search: searchQuery || undefined,
        status: filters.status !== 'all' ? filters.status : undefined,
        page: 1,
        pageSize: PAGE_SIZE,
      });
      setRows(response.results);
    } catch (_error) {
      setSnackbarMessage({ message: 'Không thể tải danh sách hoàn trả.', severity: 'error' });
    } finally {
      setListLoading(false);
    }
  }, [filters.status, listScopeCompanyId, searchQuery]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      void loadReturns();
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [loadReturns]);

  const updateFormData = (updates: Partial<InventoryReturnFormData>) => {
    if (!activeTabData?.id || !activeFormData) return;

    const isUpdateMode = Boolean(activeReturnId);
    const tabPermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    const canEditCurrentTab = isUpdateMode ? tabPermissions.canUpdate : tabPermissions.canCreate;
    if (!canEditCurrentTab) {
      return;
    }

    const nextFormData = { ...activeFormData, ...updates };
    updateTab(activeTabData.id, { metadata: buildFormMetadata(nextFormData, activeReturnId) });
    handleUnsavedChangesUpdate(true, activeTabData.id);
  };

  const openExistingReturn = (row: InventoryReturnRow) => {
    if (!canReadRecord(row)) {
      setSnackbarMessage({ message: 'Bạn không có quyền xem chi tiết phiếu hoàn trả.', severity: 'error' });
      return;
    }
    guardAndRun(() => {
      openTab({
        id: Date.now() + Math.floor(Math.random() * 1000),
        title: row.code,
        type: 'update-return',
        closable: true,
        entityId: row.id,
        metadata: {
          companyId: row.company_id,
          ...buildFormMetadata(
          {
            code: row.code,
            name: row.name,
            order_no: row.order_no,
            store_name: row.store_name,
            status: row.status,
            qty: row.qty ? String(row.qty) : '',
            company_id: row.company_id,
            note: row.note || '',
          },
          row.id
        ),
        },
      });
    });
  };

  const handleSearchInputChange = (value: string) => {
    setSearchInputValue(value);
    if (value === '') {
      setSearchQuery('');
    }
  };

  const handleSearchSubmit = () => {
    setSearchQuery(searchInputValue.trim());
  };

  const handleRefresh = () => {
    void loadReturns()
      .then(() => setSnackbarMessage({ message: 'Đã refresh danh sách hoàn trả.', severity: 'success' }))
      .catch(() => setSnackbarMessage({ message: 'Không thể refresh danh sách hoàn trả.', severity: 'error' }));
  };

  const handleSave = () => {
    if (!activeFormData || !activeTabData?.id) return;

    const isUpdateMode = Boolean(activeReturnId);
    const savePermissions = resolvePermissionsForCompanyId(activeFormData.company_id);
    if (isUpdateMode && !savePermissions.canUpdate) {
      setSnackbarMessage({ message: 'Bạn không có quyền cập nhật phiếu hoàn trả.', severity: 'error' });
      return;
    }
    if (!isUpdateMode && !savePermissions.canCreate) {
      setSnackbarMessage({ message: 'Bạn không có quyền tạo phiếu hoàn trả.', severity: 'error' });
      return;
    }

    const company = companyOptions.find((entry) => entry.company_id === activeFormData.company_id);
    const nextRow: InventoryReturnRow = {
      id: activeReturnId || `rt-${Date.now()}`,
      code: activeFormData.code,
      name: activeFormData.name,
      order_no: activeFormData.order_no,
      store_name: activeFormData.store_name,
      status: activeFormData.status,
      qty: Number(activeFormData.qty || 0),
      company_id: activeFormData.company_id,
      company_name: company?.company_name || 'Chưa rõ công ty',
      note: activeFormData.note,
    };

    setRows((current) =>
      current.some((row) => row.id === nextRow.id)
        ? current.map((row) => (row.id === nextRow.id ? nextRow : row))
        : [nextRow, ...current]
    );

    handleUnsavedChangesUpdate(false, activeTabData.id);
    setSnackbarMessage({ message: `Đã lưu ${nextRow.code || nextRow.name}.`, severity: 'success' });
    updateTab(activeTabData.id, {
      title: nextRow.code || nextRow.name,
      entityId: nextRow.id,
      metadata: buildFormMetadata(activeFormData, nextRow.id),
    });
  };

  const formCompanyOptions = useMemo(() => {
    if (!activeFormData?.company_id) {
      return companyOptions;
    }
    const exists = companyOptions.some((company) => company.company_id === activeFormData.company_id);
    if (exists) {
      return companyOptions;
    }
    return [
      {
        company_id: activeFormData.company_id,
        company_name: activeFormData.company_id,
      },
      ...companyOptions,
    ];
  }, [activeFormData?.company_id, companyOptions]);

  return (
    <>
      <ModuleTabsHeader
        tabs={tabs}
        activeTab={visibleActiveTab}
        currentTabType={activeTabData?.type}
        onTabChange={handleVisibleTabChange}
        onCloseTab={handleCloseTab}
        onAddTab={handleAddTab}
        moduleType='inventory'
        unsavedDialog={unsavedDialog}
      />

      <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', width: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex' }}>
          <Box sx={{ position: 'relative', width: '100%' }}>
            {storedTabs.map((tab, index) => {
              if (!isTabVisibleForToolbarCompany(tab, toolbarTabCompanyFilter)) {
                return null;
              }
              const isActive = isStoredTabPanelActive(tab);
              const tabFormData = tab.metadata?.formData as InventoryReturnFormData | undefined;
              const tabPermissions = resolveTabPermissions(tab);

              return (
                <Box key={tab.id} sx={{ display: isActive ? 'flex' : 'none', width: '100%', height: '100%' }}>
                  {tab.type === 'list' ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
                      <MainListModule
                        leftSidebar={null}
                        toolbar={
                          <AppListToolbarGlobal
                            searchQuery={searchInputValue}
                            onSearchChange={handleSearchInputChange}
                            onSearchSubmit={handleSearchSubmit}
                            onToggleAdvancedSearch={() => setIsAdvancedSearchOpen((current) => !current)}
                            isAdvancedSearchOpen={isAdvancedSearchOpen}
                            filters={{
                              company: filters.company || 'all',
                              status: filters.status,
                            }}
                            onFilterChange={handleToolbarFilterChange}
                            statusOptions={returnStatusOptions}
                            onRefresh={handleRefresh}
                          />
                        }
                        dataTable={
                          <ReturnList
                            ref={listRef}
                            rows={rows}
                            loading={listLoading}
                            onSelectRow={openExistingReturn}
                            onRefresh={() => void loadReturns()}
                          />
                        }
                      />
                    </Box>
                  ) : tabFormData ? (
                    <Box sx={{ width: '100%', height: '100%', overflow: 'auto', p: 2 }}>
                      <ReturnForm
                        formData={tabFormData}
                        companyDisabled={!hasGlobalRole}
                        canEdit={tab.type === 'new-return' ? tabPermissions.canCreate : tabPermissions.canUpdate}
                        companyOptions={formCompanyOptions}
                        statusOptions={formStatusOptions}
                        onChangeForm={(updates) => {
                          const canEditTab = tab.type === 'new-return' ? tabPermissions.canCreate : tabPermissions.canUpdate;
                          if (!canEditTab) {
                            return;
                          }
                          if (tab.id !== activeTabData?.id) {
                            updateTab(tab.id, {
                              metadata: buildFormMetadata(
                                { ...tabFormData, ...updates },
                                tab.metadata?.returnId as string | undefined
                              ),
                            });
                            handleUnsavedChangesUpdate(true, tab.id);
                            return;
                          }
                          updateFormData(updates);
                        }}
                        onSave={handleSave}
                      />
                    </Box>
                  ) : null}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      <Snackbar open={Boolean(snackbarMessage)} autoHideDuration={3500} onClose={() => setSnackbarMessage(null)}>
        <Alert severity={snackbarMessage?.severity || 'success'} onClose={() => setSnackbarMessage(null)} sx={{ width: '100%' }}>
          {snackbarMessage?.message}
        </Alert>
      </Snackbar>
    </>
  );
}
